import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useI18n } from '../../../i18n'
import { useAuth } from '../../AuthProvider'
import { fetchControlExecutionDetail, type ControlExecutionDetail } from '../../../services/controlCentricWorkflowApi'
import { ControlQuestionAnswerCard, type QuestionRow } from './ControlQuestionAnswerCard'
import { ControlContextPanel } from './ControlContextPanel'

type Lang = 'en' | 'ru' | 'es'

type Props = {
  sessionId: string
  controlKey: string
  standardCode: string
  domainKey: string
  lang: Lang
  govEvidence?: Array<{ id: string; title?: string; freshnessStatus?: string }>
  onSaveQuestionAnswer: (params: {
    domainKey: string
    questionKey: string
    canonicalControlCode: string | null
    answerValue: string
    assessorNote: string
  }) => Promise<void>
  onRefresh?: () => void
}

export function ControlExecutionWorkspace({
  sessionId,
  controlKey,
  standardCode,
  domainKey,
  lang,
  govEvidence,
  onSaveQuestionAnswer,
  onRefresh,
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [detail, setDetail] = useState<ControlExecutionDetail | null>(null)
  const [bootLoading, setBootLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answer, setAnswer] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const overviewRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasLoadedRef = useRef(false)

  const load = useCallback(
    async (silent = false) => {
      if (!token) return
      setError(null)
      if (!silent) {
        if (!hasLoadedRef.current) setBootLoading(true)
        else setRefreshing(true)
      }
      try {
        const data = await fetchControlExecutionDetail(token, sessionId, controlKey, lang)
        setDetail(data)
        hasLoadedRef.current = true
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load control')
      } finally {
        setBootLoading(false)
        setRefreshing(false)
      }
    },
    [token, sessionId, controlKey],
  )

  useEffect(() => {
    hasLoadedRef.current = false
    setDetail(null)
    setBootLoading(true)
    void load(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, controlKey])

  const primaryQuestion: QuestionRow | undefined = detail?.questions?.[0]

  useEffect(() => {
    if (!primaryQuestion) return
    const av = String(primaryQuestion.answerValue || '').toUpperCase()
    if (['YES', 'PARTIAL', 'NO', 'NOT_APPLICABLE', 'N/A'].includes(av)) {
      setAnswer(av === 'N/A' ? 'NOT_APPLICABLE' : av)
    } else setAnswer('')
    setNote(primaryQuestion.assessorNote || '')
    setSaveError(null)
  }, [primaryQuestion?.questionKey, primaryQuestion?.answerValue, primaryQuestion?.assessorNote])

  const scheduleOverviewRefresh = useCallback(() => {
    if (overviewRefreshTimerRef.current) clearTimeout(overviewRefreshTimerRef.current)
    overviewRefreshTimerRef.current = setTimeout(() => onRefresh?.(), 600)
  }, [onRefresh])

  useEffect(
    () => () => {
      if (overviewRefreshTimerRef.current) clearTimeout(overviewRefreshTimerRef.current)
    },
    [],
  )

  if (bootLoading && !detail) {
    return <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
  }
  if (error && !detail) return <p className="text-sm text-red-600">{error}</p>
  if (!detail?.enabled) return null

  if (!primaryQuestion) {
    return (
      <p className="text-sm text-slate-600">{t('controlCentric.sequential.noSteps', 'No assessment questions available.')}</p>
    )
  }

  const sufficiencyLabel = detail.evidenceSufficiency
    ? t(`complianceEvidence.sufficiency.${detail.evidenceSufficiency}`, detail.evidenceSufficiency)
    : null

  return (
    <div className="space-y-4">
      <ControlContextPanel
        controlKey={controlKey}
        title={detail.title}
        description={detail.description}
        soc2Criteria={detail.soc2Criteria}
        criteriaReference={detail.criteriaReference ?? detail.soc2Criteria}
        standardCode={standardCode}
        evidenceExpectations={detail.evidenceExpectations}
        refreshing={refreshing}
        onRefresh={() => void load(true)}
      />

      <ControlQuestionAnswerCard
        lang={lang}
        sessionId={sessionId}
        standardCode={standardCode}
        question={primaryQuestion}
        evidenceSufficiencyLabel={sufficiencyLabel}
        recommendation={detail.recommendation}
        expectedEvidenceHint={detail.expectedEvidenceHint}
        govEvidence={govEvidence}
        answer={answer}
        note={note}
        saving={saving}
        saveError={saveError}
        onAnswerChange={setAnswer}
        onNoteChange={setNote}
        onEvidenceLinkedChange={() => {
          void load(true)
          scheduleOverviewRefresh()
        }}
        onSave={async () => {
          if (!answer) {
            setSaveError(t('complianceCenterUi.journey.errors.noAnswer', 'Select an answer before saving.'))
            return
          }
          setSaving(true)
          setSaveError(null)
          try {
            await onSaveQuestionAnswer({
              domainKey: primaryQuestion.domainKey || domainKey,
              questionKey: primaryQuestion.questionKey,
              canonicalControlCode: primaryQuestion.legacyControlCode || detail.legacyControlCode || null,
              answerValue: answer,
              assessorNote: note,
            })
            await load(true)
            onRefresh?.()
          } catch (e: unknown) {
            setSaveError(e instanceof Error ? e.message : t('complianceCenterUi.journey.errors.save', 'Failed to save answer.'))
          } finally {
            setSaving(false)
          }
        }}
        footer={
          refreshing ? (
            <span className="self-center text-xs text-slate-500">{t('common.refreshing', 'Refreshing…')}</span>
          ) : null
        }
      />
    </div>
  )
}
